import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  Button,
  Card,
  Typography,
  Skeleton,
  Box,
  Divider,
  InputAdornment,
} from '@mui/material';
import { Close, Search, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import TablePagination from '@mui/material/TablePagination';
import { ITechnician } from '../../utils/entities/interface';
import useSnackbar from '../../hooks/useSnackbar';
import { capitalizeWords, formatPhoneNumber } from '../../utils/helper';
import { GETHelperTechnicians, UpdatePair } from '../../services/acdHelper';

// Debounce function
export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  delay: number,
) => {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

const AssignHelperModal = ({
  isOpen,
  onClose,
  bookingId,
  onReload,
}: {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  onReload: () => void;
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [technicians, setTechnicians] = useState<ITechnician[] | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);
  const [totalTechnician, setTotalTechnician] = useState(0);
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const navigate = useNavigate();

  const handleViewTechnician = (technicianId: string) => {
    onClose();
    navigate(`/technicians/${technicianId}`);
  };

  // Debounced fetch function
  const fetchTechnicians = async (query = '') => {
    setLoading(true);
    try {
      const response = await GETHelperTechnicians({
        page: page + 1,
        limit: rowsPerPage,
        searchQuery: query,
      });
      setTechnicians(response.data);
      setTotalTechnician(response.total);
    } catch (error) {
      console.error('Error fetching technicians:', error);
    }
    setLoading(false);
  };

  // Memoized debounced function
  const debouncedFetchTechnicians = useCallback(
    debounce(fetchTechnicians, 500),
    [page, rowsPerPage],
  );

  useEffect(() => {
    if (isOpen) {
      debouncedFetchTechnicians(searchTerm);
    }
  }, [searchTerm, page, rowsPerPage, isOpen]);

  const handleTechieAssign = async (
    event: React.MouseEvent<HTMLButtonElement>,
    technicianId: string,
  ) => {
    event.preventDefault();
    try {
      const result = await UpdatePair(bookingId, technicianId);

      if (result === 200) {
        showSnackbar('Assigned successfully!', 'success');
        setTimeout(() => {
          onReload();
          onClose();
        }, 600);
      } else {
        showSnackbar('Assignment Failed!', 'error');
      }
    } catch {
      showSnackbar('Error while assigning.', 'error');
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ borderRadius: 10, overflow: 'hidden' }}
      PaperProps={{
        sx: {
          borderRadius: 10,
          overflow: 'auto',
          backgroundColor: '#1C1C1C ',
        },
      }}
    >
      <Box
        sx={{
          border: '1px solid #E31E25',
          borderRadius: 10,
          overflow: 'auto',
          backgroundColor: '#1C1C1C ',
        }}
      >
        {' '}
        {SnackbarComponent}
        <DialogTitle
          sx={{
            fontSize: '22px',
            fontWeight: 'bold',
            color: '#FFFFFF',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#1C1C1C ',
            padding: '16px',
          }}
        >
          Pair Technician
          <IconButton onClick={onClose} sx={{ color: '#FFFFFF' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ bgcolor: '#1E1E1E', color: 'white', padding: 3 }}>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
            <TextField
              variant="outlined"
              placeholder="Search Technician"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#B0B0B0' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: '50%',
                input: { color: '#E0E0E0' },
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#232323',
                  borderRadius: '8px',
                  '& fieldset': {
                    borderColor: '#232323',
                  },
                  '&:hover fieldset': {
                    borderColor: '#232323',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#232323',
                  },
                },
              }}
            />
          </div>
          {loading ? (
            Array.from(new Array(5)).map((_, index) => (
              <Card
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: '#2A2A2A',
                  color: 'white',
                  padding: 2,
                  borderRadius: 2,
                  border: 'none',
                  mb: 1,
                }}
              >
                <Skeleton variant="text" width={120} height={20} />
                <Skeleton variant="text" width={100} height={15} />
                <Skeleton variant="text" width={80} height={15} />
                <Skeleton variant="circular" width={30} height={30} />
                <Skeleton variant="rectangular" width={80} height={30} />
              </Card>
            ))
          ) : (
            <>
              {Array.isArray(technicians) && technicians.length > 0 ? (
                technicians.map((tech: ITechnician) => (
                  <Card
                    key={tech._id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      bgcolor: '#232323',
                      color: 'white',
                      padding: 2,
                      borderRadius: 2,
                      border: 'none',
                      mb: 1,
                    }}
                  >
                    <Typography variant="body1" color="#8A8D91" width={'20%'}>
                      {tech.name}
                    </Typography>

                    <Typography variant="body2" color="#8A8D91" width={'20%'}>
                      {formatPhoneNumber(tech.phoneNumber)}
                    </Typography>
                    <Typography variant="body2" color="#8A8D91" width={'15%'}>
                      {capitalizeWords(tech.position)}
                    </Typography>
                    <Typography variant="body2" color="#8A8D91">
                      {tech.level}
                    </Typography>
                    <IconButton
                      sx={{ color: '#E31E25' }}
                      onClick={() => handleViewTechnician(tech._id)}
                      aria-label="View technician profile"
                    >
                      <Visibility />
                    </IconButton>
                    <Button
                      variant="outlined"
                      color="success"
                      onClick={(e) => handleTechieAssign(e, tech._id)}
                    >
                      Assign
                    </Button>
                  </Card>
                ))
              ) : (
                <Typography
                  color="gray"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',

                    height: 300,
                    margin: 'auto',
                  }}
                >
                  No Available technicians found.
                </Typography>
              )}
            </>
          )}

          <TablePagination
            rowsPerPageOptions={[2, 5, 10, 25, 50]}
            component="div"
            count={totalTechnician}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) =>
              setRowsPerPage(parseInt(event.target.value, 10))
            }
          />
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default AssignHelperModal;

interface ModalConfirmationProps {
  open: boolean;
  handleClose: () => void;
  technician: string;
  // handleConfirm: () => void;
  // selectedItem: IUser | ITechnician | ICoupon | IBrand | null;
  // entityName?: string; // Technician / Customer / Brand / Anything
  // icon?: ReactNode; // Optional custom icon
}

export const ModalNotification = ({
  open,
  handleClose,
  technician,
}: ModalConfirmationProps) => {
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      sx={{ borderRadius: 4 }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          border: '1px solid #E31E25',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <DialogContent
          sx={{
            backgroundColor: '#181818',
            padding: 4,
            textAlign: 'center',
          }}
        >
          {/* Icon inside Circle */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              border: '2px solid #E31E25',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 3,
            }}
          >
            {/* {icon || <PersonOff sx={{ fontSize: 40, color: 'black' }} />} */}
          </Box>

          <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
            {technician + 'technician is assigned and will assist you shortly'}
          </Typography>
        </DialogContent>
      </Box>
    </Dialog>
  );
};
